import * as THREE from 'three';
import {PointCloud} from './point-cloud';
import {RawPointCloudRootNode, RawPointCloudNode} from './raw-pointcloud-node';
import {randomHexColor} from '../misc';
import {LASFile} from "../las-loader";
import {readBufferChunks} from "../file-system-loader";
import LocalScene from '../projections';

export class RawPointCloud extends PointCloud {
  private file: any = null;
  private header: any = null;
  private lasfile: any = null;
  private generator: any;
  private maxPoints: any = 5 * 1000000;
  public loaded: any = false;
  private minPointsPerNode: any = 100 * 1000;
  private debugBoxColor: any = randomHexColor();
  private totalRead: any;
  private loading: boolean;
  private timeStart: any;
  private totalToRead: any;
  private timeEnd: any;
  public canReadData: Boolean = false;
  private readingData: Boolean = false;
  private nodeDataCache = [];
  private maxCacheSize = 10;
  private worker: Worker;
  public percent;

  constructor(pointclouds) {
    super(pointclouds);

    this.worker = new Worker(`${process.env.PUBLIC_URL}/workers/pointcloud-decoder-las.js`);
  }

  get shift() {
    return LocalScene.origin;
  }

  get size() {
    return Math.max.apply(null, this.header.size);
  }

  /** Get bounding box with global shift applied */
  get tightBoundingBox() {
    let box = this._tightBoundingBox;
    if (box === null) {
      return box;
    }

    let boundingBox = {
      min: new THREE.Vector3().add(box.min).sub(this.shift),
      max: new THREE.Vector3().add(box.max).sub(this.shift)
    };

    return boundingBox;
  }

  /** Get estimated bounding box using header information */
  get estimatedBoundingBox() {
    return {
      max: new THREE.Vector3(...this.header.maxs).sub(this.shift),
      min: new THREE.Vector3(...this.header.mins).sub(this.shift)
    };
  }

  get isRemoteFile() {
    return this.file.remote;
  }

  get nodeCacheSize() {
    let numPoints = 0;
    this.nodeDataCache.forEach(data => {
      numPoints += data.sampledPoints;
    });
    return numPoints;
  }

  resetWithNewFile() {
    return;
  }

  fileLoadStart() {
    this.loading = true;
    this.loaded = false;
    this.updatePercent(0);
    this.timeStart = performance.now();
  }

  fileLoadFinish() {
    this.loading = false;
    this.loaded = true;
    this.timeEnd = performance.now();
    this.updatePercent(100);
    const elapsed = this.timeEnd - this.timeStart;
    console.log(`Finished Loading: ${this.file.name}. ${elapsed}ms`);
  }

  fileLoadCancel() {
    this.loading = false;
    this.loaded = false;
  }

  fileLoadProgress() {
    let percent = this.totalRead / this.totalToRead;
    percent = Math.round(percent * 1000) / 10.0;
    percent = Math.min(100, percent);
    this.updatePercent(percent);
  }

  updatePercent(percent) {
    this.percent = percent;
  }

  updateRootNode() {
    this.root.updateSizeInfo(this.tightBoundingBox);
  }

  generateNewNode(data) {
    // Decode raw cloud node (result is always in meters)
    let skips = Math.max(1.0, this.totalToRead / this.maxPoints);
    data.sampledPoints = Math.ceil(data.count / skips);
    this.nodeDataCache.push(data);

    return new Promise((resolve, reject) => {
      let cacheCanGrow = this.nodeDataCache.length < this.maxCacheSize;
      let needsMorePoints = this.nodeCacheSize < this.minPointsPerNode;
      if (cacheCanGrow && needsMorePoints && data.hasMoreData) {
        resolve(false);
        return;
      }

      // Get merge arraybuffer
      let {count, buffer} = this.mergeCacheArrayBuffers();

      // Decode pointcloud with webworker
      this.worker.onmessage = event => {
        let data = event.data;

        // Convert array to vector
        data.xyz_min = new THREE.Vector3(...data.xyz_min);
        data.xyz_max = new THREE.Vector3(...data.xyz_max);
        data.offset = new THREE.Vector3(...data.offset);

        // Convert raw buffer to float array
        data.positions = new Float32Array(data.positions);
        data.intensity = new Float32Array(data.intensity);
        data.classification = new Float32Array(data.classification);
        data.colors = new Float32Array(data.colors);

        if (this.enabled) {
          // Add new node to pointcloud
          let node = new RawPointCloudNode(this, this.root);
          node.loadFromData(data);
        }

        resolve(true);
      };

      this.worker.postMessage([
        buffer, count, this.header, skips
      ],[buffer]);
    });
  }

  mergeCacheArrayBuffers() {
    let totalCount = 0;
    let bufferLength = 0;
    this.nodeDataCache.forEach(node => {
      totalCount += node.count;
      bufferLength += node.buffer.byteLength;
    });

    let bufferIndex = 0;
    let buffer = new Uint8Array(bufferLength);
    this.nodeDataCache.forEach(node => {
      let nodeBuffer = new Uint8Array(node.buffer);
      buffer.set(nodeBuffer, bufferIndex);
      bufferIndex += nodeBuffer.byteLength;
    });

    // Clear cache
    this.nodeDataCache = [];

    return {
      count: totalCount,
      buffer: buffer.buffer
    };
  }

  loadFile(file) {
    this.file = file;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setIdentifier(file.identifier);
    this.setVisibility(file.visible);
    this.fileLoadStart();

    this.getBinaryLocal(file)
      .then(data => {
        let las = new LASFile(data);
        this.loadData(las);
      })
      .catch(() => {
        console.error(`Error loading pointcloud: ${file.path}`);
        this.viewer.showMessage(`Error loading pointcloud: ${file.name}`, {
          variant: "error"
        });
        this.fileMissing = true;
      });
  }

  generateWarningHTML(messages) {
    if (messages.length === 0) {
      return null;
    }

    let html = "";

    messages.forEach((item, index) => {
      //item.message = null;

      // Title / message
      html += `
        <b>Warning:</b><br>
        <ul>
          ${item.messages.map(x => `<li>${x}</li>`).join("")}
        </ul>
      `;

      // List of possible issues
      if (item.issues) {
        html += `
          <b>Possible Issues:</b>"
          <ul>
            ${item.issues.map(x => `<li>${x}</li>`).join("")}
          </ul>
        `;
      }

      // Solution (ignore for now)
      if (item.solution) {
        html += `
          <b>Proposed Fix: </b>
          <ul>
            <li>${item.solution}</li>
          </ul>
        `;
      }

      // Add seperator, except for last item
      const lastItem = index === messages.length - 1;
      if (!lastItem) {
        html += '<hr>';
      }
    });

    return html;
  }

  fileLoadFinished(success) {
    this.canReadData = false;

    if (success) {
      this.fileLoadFinish();
    } else {
      this.fileLoadCancel();
    }
  }

  getBinaryLocal(file): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.isRemoteFile) {
        console.warn("Static file loading not implemented");
        // parseFileChunks(file, startChunk, onLoad);
        reject();
      } else {
        // Load a small amount of data to parse the header
        readBufferChunks(file.path, 0, 1024, 1)
          .then(data => {
            resolve(data);
          })
          .catch(() => {
            reject();
          });
      }
    });
  }

  loadData(lf) {
    return new Promise((res, rej) => {
      lf.open()
        .then(function () {
          lf.isOpen = true;
          return lf;
        })
        .then(lf => {
          let toMeters = LocalScene.dataToMeters;
          return lf.getHeader(toMeters)
            .then(data => {
              const {header, warnings} = data;

              if (warnings.length > 0) {
                const errorHTML = this.generateWarningHTML(warnings);
                this.viewer.setCustomAlertHTML(errorHTML);
              }

              return {
                las: lf,
                header: header
              };
            });
        })
        .then(data => {
          this.resetWithNewFile();

          // Our las file object and header info
          let lf = data.las;
          let header = data.header;

          // Set some header attributes from the file
          header.name = lf.name;
          header.versionAsString = lf.versionAsString;
          header.isCompressed = lf.isCompressed;

          // Set our attributes
          this.lasfile = lf;
          this.header = header;
          this.totalRead = 0;
          this.totalToRead = header.pointsCount;

          this.root = new RawPointCloudRootNode(this);
          this.canReadData = true;
          this.readingData = false;
        });
    });
  }

  readMoreData() {
    if (this.readingData) {
      return;
    }

    this.readingData = true;
    if (!this.enabled) {
      return;
    }

    this.lasfile.getFileBuffers()
      .then(() => {
        return this.lasfile.readData();
      })
      .then(data => {
        this.processArrayBuffer(data);
      });
  }

  processArrayBuffer(data) {
    this.totalRead += data.count;

    if (!this.enabled) {
      // File has been disabled, so we stop loading
      this.fileLoadFinished(false);
      return;
    }

    this.generateNewNode(data)
      .then(nodeGenerated => {
        if (!nodeGenerated) {
          // Keep loading more data
          this.readingData = false;
          return;
        }

        if (this.enabled) {
          this.pointclouds.zoomToExtent(this.estimatedBoundingBox);
          this.pointclouds.updateMinimapExtent();
          this.fileLoadProgress();

          if (data.hasMoreData) {
            // Keep loading more data
            this.readingData = false;
            return;
          }

          // File is done loading
          this.fileLoadFinished(true);
        }
      });
  }
}

export const parseFileChunks = (file, onLoadCallback, chunk) => {
  // Load 5 chunks at 50mb each
  let maxChunks = 5;
  let chunkSize = 1024 * 1024 * 50;

  let chunks = [];
  let fileSize = file.size;
  let offset = chunkSize * chunk;
  let chunksRead = 0;
  let chunkReaderBlock = null;

  let readEventHandler = function (evt) {
    chunksRead += 1;

    if (!evt.target.error) {
      offset += evt.target.result.byteLength;
      chunks.push(evt.target.result);
    } else {
      console.log(`Read error: ${evt.target.error}`);
      return;
    }

    // File must be finished loading
    if ((offset >= fileSize) || (chunksRead === maxChunks)) {
      onLoadCallback({
        file: file,
        buffers: chunks,
        bufferChunkSize: chunkSize,
        numBytes: file.size,
        maxchunks: maxChunks
      });

      return;
    }

    // Read next chunk of data
    chunkReaderBlock(offset, chunkSize, file);
  };

  chunkReaderBlock = function (_offset, length, _file) {
    let r = new FileReader();
    let blob = _file.slice(_offset, length + _offset);
    r.onload = readEventHandler;
    r.readAsArrayBuffer(blob);
  };

  // Start the read with the first block
  chunkReaderBlock(offset, chunkSize, file);
};
